import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FaSearch } from 'react-icons/fa';
import instanciaAxios from '../../../miscelaneo/instanciaAxios';

export default function InputBusquedaRNC({
  value = '',
  onChange,
  onSeleccionar,
  placeholder = 'Buscar por RNC',
  disabled = false,
  className = ''
}) {
  const [query, setQuery] = useState(value || '');
  const [resultados, setResultados] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [mostrarResultados, setMostrarResultados] = useState(false);
  const [errorBusqueda, setErrorBusqueda] = useState('');
  const contenedorRef = useRef(null);

  useEffect(() => {
    setQuery(value || '');
  }, [value]);

  useEffect(() => {
    const handleClickFuera = (event) => {
      if (!contenedorRef.current) return;
      if (!contenedorRef.current.contains(event.target)) {
        setMostrarResultados(false);
      }
    };

    document.addEventListener('mousedown', handleClickFuera);
    return () => document.removeEventListener('mousedown', handleClickFuera);
  }, []);

  const queryNormalizado = useMemo(() => query.trim(), [query]);

  useEffect(() => {
    if (disabled) return;

    if (!queryNormalizado || queryNormalizado.length < 3) {
      setResultados([]);
      setErrorBusqueda('');
      return;
    }

    let cancelado = false;
    const timeoutId = setTimeout(async () => {
      try {
        setCargando(true);
        setErrorBusqueda('');
        const response = await instanciaAxios.get('/dgii/contribuyente/buscar', {
          params: { q: queryNormalizado, limit: 10, page: 1 }
        });

        if (!cancelado) {
          setResultados(response.data?.data || []);
          setMostrarResultados(true);
        }
      } catch (error) {
        if (!cancelado) {
          setResultados([]);
          setErrorBusqueda(error.response?.data?.mensaje || 'No se pudo consultar el padrón DGII');
        }
      } finally {
        if (!cancelado) {
          setCargando(false);
        }
      }
    }, 300);

    return () => {
      cancelado = true;
      clearTimeout(timeoutId);
    };
  }, [queryNormalizado, disabled]);

  const handleInputChange = (event) => {
    const nuevoValor = event.target.value.replace(/\D/g, '').slice(0, 11);
    setQuery(nuevoValor);
    onChange?.(nuevoValor);
    setMostrarResultados(true);
  };

  const handleSeleccion = (item) => {
    const rnc = item?.rnc || '';
    setQuery(rnc);
    onChange?.(rnc);
    onSeleccionar?.(item);
    setMostrarResultados(false);
  };

  const intentarSeleccionExactaPorRNC = async (rnc) => {
    const exactoEnResultados = resultados.find((item) => String(item?.rnc || '') === rnc);
    if (exactoEnResultados) {
      handleSeleccion(exactoEnResultados);
      return true;
    }

    try {
      const response = await instanciaAxios.get(`/dgii/contribuyente/${rnc}`);
      if (response?.data?.rnc) {
        handleSeleccion(response.data);
        return true;
      }
    } catch (error) {
      if (error?.response?.status !== 404) {
        setErrorBusqueda(error.response?.data?.mensaje || 'No se pudo consultar el padrón DGII');
      }
    }

    return false;
  };

  const handleKeyDown = async (event) => {
    if (event.key !== 'Enter' || disabled) return;

    const rnc = queryNormalizado.replace(/\D/g, '');
    if (![9, 11].includes(rnc.length)) return;

    event.preventDefault();
    await intentarSeleccionExactaPorRNC(rnc);
  };

  return (
    <div className={`relative ${className}`} ref={contenedorRef}>
      <div className="relative">
        <input
          type="text"
          value={query}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (resultados.length > 0 || errorBusqueda) {
              setMostrarResultados(true);
            }
          }}
          placeholder={placeholder}
          disabled={disabled}
          className={`w-full p-2.5 pr-10 rounded-xl text-gray-700 border-2 outline-none transition-all ${
            disabled
              ? 'border-gray-200 bg-gray-100 cursor-not-allowed'
              : 'border-red-200 focus:border-red-500 focus:ring-2 focus:ring-red-100'
          }`}
        />
        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-color-secundario">
          <FaSearch />
        </span>
      </div>

      {mostrarResultados && !disabled && (
        <div className="absolute z-30 mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-lg max-h-72 overflow-y-auto">
          {cargando && (
            <div className="px-3 py-2 text-sm text-gray-500">Buscando en padrón DGII...</div>
          )}

          {!cargando && errorBusqueda && (
            <div className="px-3 py-2 text-sm text-red-600">{errorBusqueda}</div>
          )}

          {!cargando && !errorBusqueda && queryNormalizado.length >= 3 && resultados.length === 0 && (
            <div className="px-3 py-2 text-sm text-gray-500">No se encontraron resultados</div>
          )}

          {!cargando && !errorBusqueda && resultados.map((item) => (
            <button
              key={`${item.rnc}-${item.razon_social}`}
              type="button"
              onClick={() => handleSeleccion(item)}
              className="w-full text-left px-3 py-2 hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
            >
              <div className="text-sm font-semibold text-gray-800">{item.rnc}</div>
              <div className="text-sm text-gray-700 truncate">{item.razon_social}</div>
              <div className="text-xs text-gray-500">
                Estado: {item.estado || 'N/D'}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
